/**
 * MyFormContainer Component
 *
 * A responsive container for form inputs that:
 * - Displays inputs in a grid on desktop (2 columns)
 * - Stacks inputs vertically on mobile
 *
 * USAGE:
 * ```tsx
 * <SimpleForm>
 *   <MyFormContainer>
 *     <TextInput source="firstName" />
 *     <TextInput source="lastName" />
 *     <TextInput source="email" />
 *   </MyFormContainer>
 * </SimpleForm>
 * ```
 */

import React from 'react';
import { Box, useTheme, useMediaQuery } from '@mui/material';

interface MyFormContainerProps {
  children: React.ReactNode;
  /** Number of columns on desktop (default: 2) */
  columns?: 1 | 2 | 3;
  /** Gap between items in spacing units (default: 2) */
  gap?: number;
}

const MyFormContainer: React.FC<MyFormContainerProps> = ({
  children,
  columns = 2,
  gap = 2,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr' : `repeat(${columns}, 1fr)`,
        gap: theme.spacing(gap),
        width: '100%',
        '& .MuiFormControl-root': {
          width: '100%',
        },
      }}
    >
      {children}
    </Box>
  );
};

export default MyFormContainer;
